import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { useApiMutation } from '@shared/hooks/useApiMutation';

import { addApplicationSuggestion } from '@api/applications/general';

import { DialogButton } from '@ui/uikit/components/common/Dialog/DialogLayout/internals/DialogLayout';
import { Dialog } from '@ui/uikit/components/common/DialogV2';
import { FormTextArea } from '@ui/uikit/components/common/FormTextArea';
import { Modal } from '@ui/uikit/components/common/Modal';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import styles from './AddFeedbackDialog.module.scss';

const MIN_TEXT_LENGTH = 100;
const MAX_TEXT_LENGTH = 500;

interface SuggestionFormValues {
  suggestion_text: string;
}

interface AddSuggestionDialogProps {
  applicationSlug: string;
  applicationId: number;
  isOpen: boolean;
  onOpenChange: () => void;
}

const AddSuggestionDialog = ({
  applicationSlug,
  applicationId,
  isOpen,
  onOpenChange,
}: AddSuggestionDialogProps) => {
  const form = useForm<SuggestionFormValues>({ defaultValues: { suggestion_text: '' } });
  const { control, handleSubmit, reset } = form;

  const { mutateAsync, isPending } = useApiMutation(addApplicationSuggestion, applicationSlug);

  const handleOpenChange = useCallback(() => {
    reset();
    onOpenChange();
  }, [reset, onOpenChange]);

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await mutateAsync({
        applicationId,
        payload: {
          json: {
            id: applicationId,
            suggestion_text: data.suggestion_text,
          },
        },
      });
      handleOpenChange();
    } catch (error) {
      const message = getErrorMessage(error, 'Something went wrong');
      toast.error(`Failed to add suggestion. ${message}`);
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={onOpenChange}>
      <Dialog variant="long" size="large">
        <Dialog.Header>Submit Suggestion</Dialog.Header>
        <Dialog.Body className={styles['container']}>
          <form className={styles['form']}>
            <Controller
              control={control}
              name="suggestion_text"
              rules={{
                required: { value: true, message: 'This field is required' },
                minLength: {
                  value: MIN_TEXT_LENGTH,
                  message: `Minimum length is ${MIN_TEXT_LENGTH}`,
                },
                maxLength: {
                  value: MAX_TEXT_LENGTH,
                  message: `Maximum length is ${MAX_TEXT_LENGTH}`,
                },
              }}
              render={({ field, fieldState }) => (
                <FormTextArea
                  {...field}
                  label="Suggestion"
                  placeholder="Write your suggestion"
                  isError={!!fieldState.error}
                  hint={fieldState.error?.message}
                  maxLength={MAX_TEXT_LENGTH}
                  visuallyRequired
                  rows={3}
                />
              )}
            />
          </form>
        </Dialog.Body>
        <Dialog.ButtonGroup minButtonWidth>
          <DialogButton variant="secondary" onClick={onOpenChange}>
            Close
          </DialogButton>
          <DialogButton onClick={handleFormSubmit} isLoading={isPending}>
            Add Suggestion
          </DialogButton>
        </Dialog.ButtonGroup>
      </Dialog>
    </Modal>
  );
};

export { AddSuggestionDialog };
